import {
  AccountResource,
  DeprecatedApiService,
  GroupResource,
  OrganizationHelpers,
} from '@mode/legacy/data-access-model-js';
import { BugsnagHelpers } from '~/lib/bugsnag-helpers';
import { ConnectionPermissionsService } from '~/modules/org-settings/components/connection/connection-permissions/connection-permissions.service';

import { uiRouterComponent } from '@mode/workspace-settings/feature-members-angularjs';
import { ConnectionPermissionsGroupDetailContainerComponent } from './connection-permissions/connection-permissions-group-detail/connection-permissions-group-detail-container.component';
import { ConnectionPermissionsListComponent } from './connection-permissions/connection-permissions-list/connection-permissions-list.component';
import { ConnectionPermissionsUserDetailContainerComponent } from './connection-permissions/connection-permissions-user-detail/connection-permissions-user-detail-container.component';
import { ConnectionPermissionsComponent } from './connection-permissions/connection-permissions.component';
import { ConnectionComponent } from './connection.component';

export const ConnectionRoutesConfig = [
  '$stateProvider',
  ($stateProvider) => {
    const states = [
      {
        abstract: true,
        name: 'org-settings.connection',
        url: `/data_sources/:dataSourceToken`,
        ...uiRouterComponent(ConnectionComponent),
        resolve: {
          dataSource: [
            '$stateParams',
            'ConnectionService',
            function ($stateParams, ConnectionService) {
              return ConnectionService.getDatasource($stateParams.dataSourceToken, $stateParams.orgUsername);
            },
          ],
        },
      },
      {
        abstract: true,
        name: 'org-settings.connection.permissions',
        url: `/permissions`,
        ...uiRouterComponent(ConnectionPermissionsComponent),
      },
      {
        name: 'org-settings.connection.permissions.list',
        url: '',
        ...uiRouterComponent(ConnectionPermissionsListComponent),
        params: {
          shouldShowRemoveAccessAlert: false,
        },
        resolve: {
          everyoneGroup: [
            'accountContext',
            'ConnectionPermissionsService',
            function (accountContext, permissionsService: ConnectionPermissionsService) {
              return permissionsService.getEveryoneGroup(accountContext._resource);
            },
          ],
          dataSourceCreatorToken: [
            'dataSource',
            function (dataSource) {
              return dataSource._resource.embedded('creator')?.token;
            },
          ],
          defaultAccessEntitlement: [
            'dataSource',
            'everyoneGroup',
            'ConnectionPermissionsService',
            function (dataSource, everyoneGroup: GroupResource, permissionsService: ConnectionPermissionsService) {
              return permissionsService.getDefaultEntitlement(dataSource._resource, everyoneGroup);
            },
          ],
        },
      },
      {
        name: 'org-settings.connection.permissions.user-detail',
        url: `/users/:username`,
        ...uiRouterComponent(ConnectionPermissionsUserDetailContainerComponent),
        resolve: {
          account: [
            '$stateParams',
            'AccountModel',
            function ($stateParams, AccountModel) {
              return AccountModel.find({
                username: $stateParams.username,
                useEntryPath: true,
                includes: ['groups'],
                refresh: true,
              });
            },
          ],
          accountAvatar: [
            'account',
            function (account) {
              return account.avatar;
            },
          ],
          accountIsAdmin: [
            'account',
            'accountContext',
            'ApiService',
            function (account, accountContext, ApiService: DeprecatedApiService) {
              return ApiService.getResources<AccountResource>(accountContext._resource, 'admins')
                .then(
                  (accounts: AccountResource[]) =>
                    accounts.length && accounts.some((user) => user.token === account.token)
                )
                .catch((e) => BugsnagHelpers.notifySafely(e));
            },
          ],
          accountName: [
            'account',
            function (account) {
              return account.name;
            },
          ],
          accountUserGroups: [
            'accountContext',
            'ModeCollection',
            'ApiService',
            'dataSource',
            'dataSourceEntitlements',
            '$stateParams',
            function (
              accountContext,
              ModeCollection,
              apiService: DeprecatedApiService,
              dataSource,
              dataSourceEntitlements,
              $stateParams
            ) {
              const entitledAccessors = dataSourceEntitlements.map((entitlement) => entitlement.accessor_token);
              const groupIsEntitled = (group: GroupResource) => entitledAccessors.includes(group.token);
              const groupIsNotEveryOneGroup = (group: GroupResource) =>
                !OrganizationHelpers.isOrgEveryoneGroup(accountContext._resource, group);
              const findEntitledGroups = (groups: GroupResource[]) => groups.filter(groupIsEntitled);
              const omitEveryoneGroup = (groups: GroupResource[]) => groups.filter(groupIsNotEveryOneGroup);

              return apiService
                .getResources<GroupResource>(accountContext._resource, 'users_groups_with_data_source_entitlements', {
                  relationAs: 'groups',
                  expand: {
                    username: $stateParams.username,
                    token: dataSource.token,
                  },
                })
                .then(omitEveryoneGroup)
                .then(findEntitledGroups);
            },
          ],
          dataSourceEntitlements: [
            'dataSource',
            'ApiService',
            function (dataSource, ApiService) {
              return ApiService.getResources(dataSource._resource, 'permissions', {
                relationAs: 'data_source_entitlements',
              });
            },
          ],
          dataSourceEntitlement: [
            'dataSourceEntitlements',
            'account',
            function (dataSourceEntitlements, account) {
              const isAccountEntitlement = (entitlement) => entitlement.accessor_token === account.token;

              const accountsEntitlment = dataSourceEntitlements.find(isAccountEntitlement);
              // in order to include the "overall_access", follow the accounts self link
              return accountsEntitlment ? accountsEntitlment.links('self').$follow() : null;
            },
          ],
        },
      },
      {
        name: 'org-settings.connection.permissions.group-detail',
        url: `/groups/:groupToken`,
        ...uiRouterComponent(ConnectionPermissionsGroupDetailContainerComponent),
        resolve: {
          userGroup: [
            '$stateParams',
            'GroupModel',
            function ($stateParams, GroupModel) {
              return GroupModel.find({
                token: $stateParams.groupToken,
                account: $stateParams.orgUsername,
                useEntryPath: true,
                refresh: true,
                includes: 'new_group_membership',
              });
            },
          ],
          dataSourceEntitlement: [
            'dataSource',
            'userGroup',
            'ApiService',
            function (dataSource, userGroup, ApiService) {
              const isUserGroupEntitlement = (entitlement) => entitlement.accessor_token === userGroup.token;
              return ApiService.getResources(dataSource._resource, 'permissions', {
                relationAs: 'data_source_entitlements',
              }).then((entitlements) => entitlements.find(isUserGroupEntitlement));
            },
          ],
          groupAvatar: [
            'userGroup',
            function (userGroup) {
              return userGroup.avatar;
            },
          ],
          groupName: [
            'userGroup',
            function (userGroup) {
              return userGroup.name;
            },
          ],
        },
      },
    ];

    states.forEach((state) => $stateProvider.state(state));
  },
];
